import { useEffect, useState } from 'react';
import { useTranslation } from '../../core/hooks/use-translation';
import { disablePush, enablePush, readPushState, rememberPushLanguage, type PushState } from '../../core/push/browser-push';

/**
 * The switch that turns browser notifications on for this device.
 *
 * **It renders nothing at all** where the browser cannot do it or the deployment has no key pair:
 * a control that explains why it is disabled is a control that draws attention to something the
 * customer cannot act on. An iPhone that was never added to the home screen is in that group, which
 * is a real part of the audience and the reason the panel below it is still the way notifications
 * are read.
 */
export function PushToggle() {
    const { t, language } = useTranslation();

    const [state, setState] = useState<PushState | null>(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        const controller = new AbortController();
        let current = true;

        void readPushState(controller.signal).then((next) => {
            if (current) {
                setState(next);
            }
        });

        // The worker composes its sentence in the language the portal is being read in, and cannot
        // read `localStorage` to find out which that is.
        void rememberPushLanguage(language);

        return () => {
            current = false;
            controller.abort();
        };
    }, [language]);

    if (state === null || !state.supported || !state.available) {
        return null;
    }

    async function toggle() {
        setBusy(true);

        try {
            setState(await (state?.subscribed ? disablePush() : enablePush()));
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="mt-4 flex items-center justify-between gap-3 border-t border-slate-200 pt-3 dark:border-slate-800">
            <span className="text-xs text-slate-500 dark:text-slate-400">
                {t(state.blocked ? 'portal.push_blocked' : 'portal.push_hint')}
            </span>

            {!state.blocked && (
                <button
                    type="button"
                    onClick={() => void toggle()}
                    disabled={busy}
                    className="shrink-0 text-xs font-semibold text-brand-700 hover:underline disabled:opacity-50 dark:text-brand-400"
                >
                    {t(state.subscribed ? 'portal.push_disable' : 'portal.push_enable')}
                </button>
            )}
        </div>
    );
}
